import { apiFetchJson, apiPost, apiPatch, apiDelete } from './api';

/**
 * Fetch all clients with optional filtering, sorting, and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.page_size - Number of items per page
 * @param {string} params.ordering - Field to sort by (prefix with - for descending)
 * @param {string} params.search - Search query
 * @param {boolean} params.is_active - Filter by active status
 * @returns {Promise<Object>} Response with results array and pagination info
 */
export const fetchClients = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  if (params.search) queryParams.append('search', params.search);
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);

  return apiFetchJson(`/api/data-loads/clients/?${queryParams.toString()}`);
};

/**
 * Fetch a single client by ID
 * @param {string} clientId - Client UUID
 * @returns {Promise<Object>} Client object
 */
export const fetchClient = async (clientId) => {
  return apiFetchJson(`/api/data-loads/clients/${clientId}/`);
};

/**
 * Create a new client
 * @param {Object} clientData - Client data
 * @param {string} clientData.name - Client name
 * @param {boolean} clientData.is_active - Whether client is active (default: true)
 * @returns {Promise<Object>} Created client object
 */
export const createClient = async (clientData) => {
  return apiPost('/api/data-loads/clients/', clientData);
};

/**
 * Update an existing client
 * @param {string} clientId - Client UUID
 * @param {Object} clientData - Updated client data
 * @returns {Promise<Object>} Updated client object
 */
export const updateClient = async (clientId, clientData) => {
  return apiPatch(`/api/data-loads/clients/${clientId}/`, clientData);
};

/**
 * Delete (deactivate) a client
 * @param {string} clientId - Client UUID
 * @returns {Promise<Object>} Response message
 */
export const deleteClient = async (clientId) => {
  return apiDelete(`/api/data-loads/clients/${clientId}/`);
};

/**
 * Get integrations for a specific client
 * @param {string} clientId - Client UUID
 * @returns {Promise<Array>} Array of integration objects
 */
export const fetchClientIntegrations = async (clientId) => {
  return apiFetchJson(`/api/data-loads/clients/${clientId}/integrations/`);
};

/**
 * Get all integrations with their revisions and load events in a single bulk API call
 * This is optimized to reduce the number of API calls needed to load the integration details page
 * @param {string} clientId - Client UUID
 * @returns {Promise<Object>} Object containing client info and all integrations with revisions
 */
export const fetchClientIntegrationsBulk = async (clientId) => {
  return apiFetchJson(`/api/data-loads/clients/${clientId}/integrations/bulk/`);
};
